import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TableCell,
  TableRow,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import api from '../config/api';
import DataTable from '../components/DataTable';

function ProvidingServices() {
  const [subcategories, setSubcategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    category_id: '',
  });
  const [error, setError] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/categories');
      const categoriesData = response.data.data || [];
      setCategories(categoriesData);
      
      // Flatten all subcategories
      const allSubcategories = [];
      categoriesData.forEach(cat => {
        if (cat.subcategories) {
          cat.subcategories.forEach(sub => {
            allSubcategories.push({ ...sub, category_name: cat.name, category_id: cat.id });
          });
        }
      });
      setSubcategories(allSubcategories);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Error fetching subcategories');
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (subcategory = null) => {
    if (subcategory) {
      setEditing(subcategory);
      setFormData({
        name: subcategory.name || '',
        category_id: subcategory.category_id || '',
      });
    } else {
      setEditing(null);
      setFormData({
        name: '',
        category_id: '',
      });
    }
    setOpen(true);
    setError('');
  };

  const handleClose = () => {
    setOpen(false);
    setEditing(null);
    setFormData({
      name: '',
      category_id: '',
    });
    setError('');
  };

  const handleSubmit = async () => {
    try {
      setError('');
      
      // Check for duplicate subcategory names within the same category (case-insensitive)
      const trimmedName = formData.name.trim();
      const duplicate = subcategories.find(
        (sub) => 
          sub.category_id === parseInt(formData.category_id) &&
          sub.name.toLowerCase() === trimmedName.toLowerCase() &&
          (!editing || sub.id !== editing.id)
      );
      
      if (duplicate) {
        setError('A subcategory with this name already exists in the selected category');
        return;
      }

      if (editing) {
        await api.put(`/subcategories/${editing.id}`, { name: trimmedName });
      } else {
        await api.post(`/categories/${formData.category_id}/subcategories`, { name: trimmedName });
      }
      handleClose();
      fetchData();
    } catch (error) {
      setError(error.response?.data?.error || error.response?.data?.message || 'Error saving subcategory');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this subcategory?')) {
      try {
        await api.delete(`/categories/subcategories/${id}`);
        fetchData();
      } catch (error) {
        console.error('Error deleting subcategory:', error);
        alert(error.response?.data?.error || error.response?.data?.message || 'Error deleting subcategory');
      }
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Providing Services</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
          Add Subcategory
        </Button>
      </Box>

      <DataTable
        data={subcategories}
        loading={loading}
        searchFields={['name', 'category_name']}
        emptyMessage="No subcategories found"
        columns={[
          { field: 'id', label: 'ID' },
          { field: 'name', label: 'Name' },
          { field: 'category_name', label: 'Category' },
          { field: 'actions', label: 'Actions' },
        ]}
        renderRow={(subcategory) => (
          <TableRow key={subcategory.id}>
            <TableCell>{subcategory.id}</TableCell>
            <TableCell>{subcategory.name}</TableCell>
            <TableCell>{subcategory.category_name || '-'}</TableCell>
            <TableCell>
              <IconButton size="small" onClick={() => handleOpen(subcategory)}>
                <EditIcon />
              </IconButton>
              <IconButton size="small" onClick={() => handleDelete(subcategory.id)}>
                <DeleteIcon />
              </IconButton>
            </TableCell>
          </TableRow>
        )}
      />

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Subcategory' : 'Add Subcategory'}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <FormControl fullWidth margin="normal" required>
            <InputLabel>Category</InputLabel>
            <Select
              value={formData.category_id}
              onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
              label="Category"
              disabled={!!editing}
            >
              <MenuItem value="">Select a category</MenuItem>
              {categories.map((cat) => (
                <MenuItem key={cat.id} value={cat.id}>
                  {cat.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Subcategory Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            margin="normal"
            required
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            disabled={!formData.name.trim() || !formData.category_id}
          >
            {editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ProvidingServices;

